/* MolCellQuest 問題データ — 実験手法・データ解釈
   ★ 配列に追記すればそのまま反映されます（id は全体で一意に）。
   ★ diff : 80〜320。基礎 0-179 / 標準 180-269 / 応用 270+ */

const CAT = { track: "applied", category: "technique", categoryName: "実験手法・データ解釈" };

export const APPLIED_TECHNIQUE = [
  {
    id: "tec01", ...CAT,
    title: "PCRの3ステップ", diff: 105, tags: ["PCR", "アニーリング", "Taqポリメラーゼ"],
    explain: `<p>PCRの1サイクルは3つの温度ステップからなる。</p>
              <p>「約55〜60℃」のステップで起きていることはどれか。</p>`,
    type: "radio",
    options: [
      "二本鎖DNAが1本ずつに解離する（変性）",
      "プライマーが鋳型に結合する（アニーリング）",
      "DNAポリメラーゼが新しい鎖を伸長する（伸長）",
      "産物が分解される",
    ],
    answer: 1,
    hints: [
      "94℃、55℃、72℃ の3つがそれぞれ何に対応するか思い出しましょう。",
      "20塩基程度の短いプライマーが安定に結合できる温度帯です。",
    ],
    solution: `<p><b>【ステップ1】3ステップの対応</b></p>
               <table>
                 <tr><th>温度</th><th>ステップ</th><th>起きること</th></tr>
                 <tr><td>約94〜98℃</td><td>変性</td><td>水素結合が切れ、二本鎖が1本鎖に</td></tr>
                 <tr><td><b>約55〜65℃</b></td><td><b>アニーリング</b></td><td><b>プライマーが相補配列に結合</b></td></tr>
                 <tr><td>約72℃</td><td>伸長</td><td>Taqポリメラーゼが 5'→3' に合成</td></tr>
               </table>
               <p><b>【ステップ2】アニーリング温度の決め方</b></p>
               <p>プライマーの融解温度 Tm より <b>約5℃低く</b>設定するのが目安です。</p>
               <ul>
                 <li><b>高すぎる</b>：プライマーが結合できず、増幅されない</li>
                 <li><b>低すぎる</b>：不完全な相補性でも結合してしまい、<b>非特異的な産物</b>が出る</li>
               </ul>
               <p>期待しないバンドが出たときに、まずアニーリング温度を上げてみるのは
               この理屈によります。</p>
               <p><b>【ステップ3】なぜ Taq ポリメラーゼなのか</b></p>
               <p>毎サイクル94℃に加熱するため、普通の酵素は失活してしまいます。
               温泉に生息する好熱菌 <i>Thermus aquaticus</i> の
               <b>耐熱性</b>DNAポリメラーゼを使うことで、
               酵素を毎回足す必要がなくなり自動化が可能になりました。</p>
               <p class="tiny">なお Taq には校正機能（3'→5'エキソヌクレアーゼ活性）がありません。
               配列の正確さが要るクローニングには、
               校正機能を持つ KOD や Pfu を使います。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec02", ...CAT,
    title: "電気泳動でDNAが動く向き", diff: 115, tags: ["電気泳動", "アガロースゲル", "分子量"],
    explain: `<p>アガロースゲル電気泳動でDNAを分離するとき、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "DNAは正に帯電しているので陰極へ移動し、大きい断片ほど速く動く。",
      "DNAは負に帯電しているので陽極へ移動し、小さい断片ほど速く動く。",
      "DNAは負に帯電しているので陽極へ移動し、大きい断片ほど速く動く。",
      "DNAは電荷を持たないので、拡散によってのみ移動する。",
    ],
    answer: 1,
    hints: [
      "DNAのリン酸基は生理的pHでどちらに帯電しているでしょうか。",
      "ゲルは網目です。大きい分子と小さい分子、どちらが通り抜けやすいでしょう。",
    ],
    solution: `<p><b>【ステップ1】電荷と移動方向</b></p>
               <p>DNAのリン酸骨格は<b>負に帯電</b>しているので、
               <b>陽極（＋）へ移動</b>します。</p>
               <p><b>【ステップ2】なぜ長さで分離できるのか</b></p>
               <p>ここが重要な点です。DNAは<b>長さに比例して電荷も増える</b>ため、
               電荷／質量比はどの長さでもほぼ一定です。
               したがって<b>自由溶液中では長さによる分離が起こりません</b>。</p>
               <p>分離を生んでいるのは<b>ゲルの網目</b>です。
               小さい断片は網目をすり抜けやすく<b>速く</b>、
               大きい断片は引っかかって<b>遅い</b>。
               つまり篩（ふるい）としての効果が分離の実体です。</p>
               <p><b>【ステップ3】移動度と長さの関係</b></p>
               <p>移動距離は分子量の<b>対数</b>にほぼ反比例します。
               だから分子量マーカーの移動距離を片対数グラフにプロットすると
               直線に乗り、未知断片の長さを内挿で求められます。</p>
               <p><b>【ステップ4】使い分け</b></p>
               <table>
                 <tr><th>ゲル</th><th>適する範囲</th></tr>
                 <tr><td>アガロース 0.7%</td><td>大きい断片（1〜20 kb）</td></tr>
                 <tr><td>アガロース 2%</td><td>小さい断片（0.1〜2 kb）</td></tr>
                 <tr><td>ポリアクリルアミド</td><td>さらに小さい断片、1塩基差の分離</td></tr>
               </table>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec03", ...CAT,
    title: "ブロッティング法の使い分け", diff: 150, tags: ["ウェスタン", "ノーザン", "サザン"],
    explain: `<p>検出したい分子とブロッティング法の組み合わせとして、正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "DNA：ノーザン ／ RNA：サザン ／ タンパク質：ウェスタン",
      "DNA：サザン ／ RNA：ノーザン ／ タンパク質：ウェスタン",
      "DNA：ウェスタン ／ RNA：サザン ／ タンパク質：ノーザン",
      "DNA：サザン ／ RNA：ウェスタン ／ タンパク質：ノーザン",
    ],
    answer: 1,
    hints: [
      "サザンは開発者 Edwin Southern の名前です。他は方角の名前をもじった洒落です。",
      "「S(outhern) = DNA」を起点に、N→RNA、W→タンパク質と覚えます。",
    ],
    solution: `<p><b>【ステップ1】名前の由来から覚える</b></p>
               <table>
                 <tr><th>手法</th><th>検出対象</th><th>プローブ／検出</th><th>名前の由来</th></tr>
                 <tr><td><b>サザン</b></td><td><b>DNA</b></td><td>標識核酸プローブ</td><td>開発者 <b>Edwin Southern</b>（人名）</td></tr>
                 <tr><td><b>ノーザン</b></td><td><b>RNA</b></td><td>標識核酸プローブ</td><td>Southern に対する洒落</td></tr>
                 <tr><td><b>ウェスタン</b></td><td><b>タンパク質</b></td><td>抗体</td><td>同上</td></tr>
               </table>
               <p>覚え方の要は「サザンだけが実在の人名」という点です。
               ここを起点にすれば、残りはアルファベット順（S→N→W）ではなく
               <b>セントラルドグマの順（DNA→RNA→タンパク質）</b>に対応させられます。</p>
               <p><b>【ステップ2】共通する手順</b></p>
               <ol>
                 <li>電気泳動で分離する</li>
                 <li>ゲルからメンブレンへ<b>転写（ブロット）</b>する</li>
                 <li>プローブ（核酸）または抗体で目的の分子を検出する</li>
               </ol>
               <p><b>【ステップ3】何が分かるか</b></p>
               <ul>
                 <li><b>サザン</b>：遺伝子のコピー数、再構成、制限酵素断片長多型（RFLP）</li>
                 <li><b>ノーザン</b>：転写産物の<b>量</b>と<b>サイズ</b>
                     （スプライシングバリアントの検出に強い）</li>
                 <li><b>ウェスタン</b>：タンパク質の量、分子量、修飾（リン酸化特異抗体を使えば）</li>
               </ul>
               <p class="tiny">現在ではノーザンは qPCR や RNA-seq に、
               サザンはシークエンシングに置き換わることが多いですが、
               「サイズ情報が同時に得られる」点は今も固有の利点です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec04", ...CAT,
    title: "対照実験の設計", diff: 165, tags: ["対照", "実験計画", "ウェスタン"],
    explain: `<p>ある薬剤がタンパク質 X の発現量を変化させるかをウェスタンブロットで調べる。</p>
              <p>この実験で<b>必ず必要な</b>対照として、最も重要なものはどれか。</p>`,
    type: "radio",
    options: [
      "薬剤を溶かした溶媒（ビヒクル）のみを加えた群と、アクチンなどのローディングコントロール",
      "タンパク質 X の精製標品のみ",
      "抗体を加えない条件のみ",
      "別の細胞株での結果のみ",
    ],
    answer: 0,
    hints: [
      "薬剤はDMSOなどに溶かします。効果が薬剤によるものか溶媒によるものか、どう区別しますか。",
      "各レーンに載せたタンパク質の総量が同じでなければ、バンドの濃さを比較できません。",
    ],
    solution: `<p><b>【ステップ1】2種類の対照が必要</b></p>
               <p><b>(1) ビヒクル対照</b><br>
               薬剤は DMSO などの溶媒に溶かして加えます。
               「薬剤あり」と「何も加えない」を比べると、
               観察された変化が<b>薬剤によるものか溶媒によるものか</b>区別できません。
               DMSO は数%でも細胞に影響します。
               だから比較すべき相手は<b>同濃度の溶媒だけを加えた群</b>です。</p>
               <p><b>(2) ローディングコントロール</b><br>
               各レーンに載せたタンパク質量が揃っている保証がありません。
               ピペッティング誤差や細胞数の違いで、
               載せた総量が2倍違えばバンドも2倍濃くなります。</p>
               <p>そこで発現量が変動しにくいタンパク質
               （β-アクチン、GAPDH、α-チューブリン）を同時に検出し、
               <b>その比でX の量を正規化</b>します。</p>
               <p><b>【ステップ2】ローディングコントロール選びの落とし穴</b></p>
               <p>「変動しにくい」は条件依存です。GAPDH は解糖系の酵素なので、
               低酸素や代謝を変える処理では<b>それ自体が変動します</b>。
               その場合、総タンパク質染色（Ponceau S、スタインフリー）のほうが適切です。</p>
               <p><b>【ステップ3】他の選択肢の位置づけ</b></p>
               <ul>
                 <li>B ⇒ 抗体の特異性やバンド位置の確認には有用ですが、
                     <b>発現量の変化</b>を論じるには不十分です。</li>
                 <li>C ⇒ 二次抗体の非特異的結合を見る対照で、有用ですが優先度は下がります。</li>
                 <li>D ⇒ 再現性の確認としては価値がありますが、
                     この実験系そのものの妥当性を担保する対照ではありません。</li>
               </ul>
               <p class="tiny">対照の原則：<b>「調べたい1つの要因だけが違う」比較を作る</b>こと。
               それ以外はすべて揃えます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "tec05", ...CAT,
    title: "CRISPR-Cas9 の標的認識", diff: 225, tags: ["CRISPR", "gRNA", "PAM"],
    explain: `<p>CRISPR-Cas9 が特定のゲノム配列を切断する仕組みについて、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "Cas9 タンパク質自身が標的の塩基配列を直接認識する。",
      "ガイドRNA が標的DNAと塩基対を作って認識し、隣接する PAM 配列の存在が切断に必須である。",
      "制限酵素と同じく、決まった6塩基の回文配列を認識する。",
      "標的の認識には抗体を使う。",
    ],
    answer: 1,
    hints: [
      "CRISPR が「プログラム可能」と言われるのはなぜでしょう。何を変えれば標的を変えられますか。",
      "PAM（Protospacer Adjacent Motif）がないと、Cas9 は結合しても切断できません。",
    ],
    solution: `<p><b>【ステップ1】認識の主役はRNA</b></p>
               <p>標的を決めているのは<b>ガイドRNA（gRNA）</b>の約20塩基で、
               これが標的DNAと塩基対を形成します。
               Cas9 は「はさみ」であって、配列の識別はしていません。</p>
               <p>ここが革命的でした。制限酵素は認識配列が固定なので、
               新しい標的にはタンパク質そのものを設計し直す必要があります
               （ZFN や TALEN もそうでした）。<br>
               CRISPR では <b>20塩基のRNAを合成し直すだけ</b>で標的を変えられます。
               設計コストが桁違いに下がったことが、爆発的な普及の理由です。</p>
               <p><b>【ステップ2】PAM という第二の条件</b></p>
               <p>gRNA が合っているだけでは切断されません。
               標的配列の 3' 側直後に <b>PAM 配列</b>
               （SpCas9 では <b>5'-NGG-3'</b>）が必要です。</p>
               <p>PAM には2つの意味があります。</p>
               <ul>
                 <li><b>設計上の制約</b>：狙える場所が PAM の近傍に限られる</li>
                 <li><b>細菌にとっての自己・非自己の識別</b>：
                     細菌自身の CRISPR 座位（スペーサーの記録）には PAM がないため、
                     <b>自分のゲノムを切ってしまわない</b></li>
                 </ul>
               <p>もともと細菌のウイルス防御機構だったことを思えば、
               この自己識別の仕組みは必然です。</p>
               <p><b>【ステップ3】切断のあと</b></p>
               <p>Cas9 は PAM の3塩基上流付近で平滑末端の二本鎖切断を作ります。
               その後は細胞の修復機構に委ねられます。</p>
               <table>
                 <tr><th>修復経路</th><th>結果</th><th>用途</th></tr>
                 <tr><td>NHEJ</td><td>ランダムな挿入・欠失 → フレームシフト</td><td><b>遺伝子ノックアウト</b></td></tr>
                 <tr><td>HDR</td><td>ドナーDNAを鋳型に正確に置換</td><td><b>ノックイン・変異導入</b></td></tr>
               </table>
               <p class="tiny">HDR は S/G2期でしか働かないため効率が低く、
               近年は塩基編集（base editing）やプライム編集など、
               二本鎖切断を作らない手法も開発されています。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec06", ...CAT,
    title: "定量PCRの ΔΔCt を計算する", diff: 255, tags: ["qPCR", "ΔΔCt", "計算"],
    explain: `<p>薬剤処理による遺伝子 X の発現変化を qPCR で調べた。
              内部標準は GAPDH である。</p>
              <table>
                <tr><th>条件</th><th>X の Ct</th><th>GAPDH の Ct</th></tr>
                <tr><td>対照</td><td>25.0</td><td>20.0</td></tr>
                <tr><td>薬剤処理</td><td>23.0</td><td>20.0</td></tr>
              </table>
              <p>ΔΔCt法（増幅効率100%）で計算したとき、処理群での X の発現量は対照の何倍か。</p>`,
    type: "radio",
    options: ["0.25 倍", "0.5 倍", "2 倍", "4 倍"],
    answer: 3,
    hints: [
      "ΔCt = 目的遺伝子の Ct − 内部標準の Ct。それぞれの群で計算しましょう。",
      "ΔΔCt = ΔCt(処理) − ΔCt(対照)。倍率は 2^(−ΔΔCt) です。",
    ],
    solution: `<p><b>【ステップ1】各群の ΔCt を求める</b></p>
               <p>ΔCt = Ct(目的) − Ct(内部標準)</p>
               <ul>
                 <li>対照：25.0 − 20.0 = <b>5.0</b></li>
                 <li>処理：23.0 − 20.0 = <b>3.0</b></li>
               </ul>
               <p><b>【ステップ2】ΔΔCt を求める</b></p>
               <p>ΔΔCt = ΔCt(処理) − ΔCt(対照) = 3.0 − 5.0 = <b>−2.0</b></p>
               <p><b>【ステップ3】倍率に変換する</b></p>
               <p>相対発現量 = 2<sup>−ΔΔCt</sup> = 2<sup>−(−2.0)</sup> = 2<sup>2</sup> = <b>4 倍</b></p>
               <p><b>【ステップ4】なぜ 2 のべき乗なのか</b></p>
               <p>PCR は1サイクルごとにDNAが<b>2倍</b>になります（効率100%のとき）。
               Ct はある閾値に達するまでのサイクル数なので、
               <b>Ct が1つ小さい＝もともと2倍多かった</b>ことを意味します。</p>
               <p>本問では処理群の Ct が2サイクル早い。
               2サイクル = 2² = 4倍です。<b>Ct が小さいほど発現量は多い</b>——
               この向きを取り違えやすいので注意しましょう。</p>
               <p><b>【ステップ5】この計算法の前提</b></p>
               <ul>
                 <li>目的遺伝子と内部標準の<b>増幅効率がほぼ等しい</b>こと
                     （希釈系列で検量線を引いて確認する）</li>
                 <li>内部標準が処理によって変動しないこと
                     （本問では GAPDH が両群とも 20.0 で一定なので妥当）</li>
               </ul>
               <p>効率が大きく異なる場合は、ΔΔCt法ではなく
               検量線法（絶対定量）や Pfaffl 法を使います。</p>
               <p>したがって、正解は <b>4 倍 (D)</b> です。</p>`,
  },

  {
    id: "tec07", ...CAT,
    title: "ノックアウトの表現型をどう解釈するか", diff: 290, tags: ["ノックアウト", "冗長性", "レスキュー実験"],
    explain: `<p>ある遺伝子 <i>G</i> をノックアウトしたマウスが、
              予想に反して<b>まったく正常な表現型</b>を示した。</p>
              <p>この結果の解釈として<b>適切でない</b>ものはどれか。</p>`,
    type: "radio",
    options: [
      "G と機能的に重複する遺伝子（パラログ）が代償している可能性がある。",
      "G の機能が、実験室の飼育環境では現れない条件下でのみ必要な可能性がある。",
      "遺伝的代償応答により、関連遺伝子の発現が上昇している可能性がある。",
      "G はゲノム上に存在するが、いかなる機能も持たないことが証明された。",
    ],
    answer: 3,
    hints: [
      "「表現型が見えない」ことは「機能がない」ことの証明になるでしょうか。",
      "実験室のマウスは、餌も温度も病原体も管理された環境で暮らしています。",
    ],
    solution: `<p><b>【ステップ1】陰性の結果の限界</b></p>
               <p>これは論理の基本です。
               <b>「表現型が観察されなかった」ことは「機能がない」ことを証明しません</b>。
               検出できなかった理由はいくらでもありえます。</p>
               <p><b>【ステップ2】考えられる理由</b></p>
               <table>
                 <tr><th>理由</th><th>検証の方法</th></tr>
                 <tr><td><b>機能的冗長性</b><br>パラログが同じ働きを担う</td><td>二重・三重ノックアウトを作る</td></tr>
                 <tr><td><b>条件依存性</b><br>ストレス下でのみ必要</td><td>感染、絶食、高脂肪食、高温／低温などの負荷をかける</td></tr>
                 <tr><td><b>遺伝的代償応答</b><br>変異mRNAの分解が類似遺伝子の発現を誘導</td><td>関連遺伝子の発現を測る。KO と ノックダウンを比較する</td></tr>
                 <tr><td><b>表現型が微細</b></td><td>より感度の高い解析（行動、代謝ケージ、組織学、-omics）</td></tr>
                 <tr><td><b>遺伝的背景の影響</b></td><td>別の系統に戻し交配して調べる</td></tr>
               </table>
               <p><b>【ステップ3】遺伝的代償応答という新しい理解</b></p>
               <p>近年、<b>ノックアウトとノックダウンで表現型が食い違う</b>現象が
               多数報告されました。その一因が遺伝的代償応答です。</p>
               <p>変異により生じた<b>未成熟終止コドンを持つmRNAの分解産物</b>が引き金となり、
               配列の似た遺伝子の発現を上昇させることが分かってきました。
               つまり <b>KO したことそのものが代償を誘導している</b>のです。
               これは「KOで表現型が出ない」ことのもっともらしい説明になります。</p>
               <p><b>【ステップ4】正しい実験の作法</b></p>
               <p>逆に<b>表現型が出た</b>場合も、それだけでは不十分です。
               <b>レスキュー実験</b>——野生型の <i>G</i> を戻して表現型が回復するか——
               を行わないと、オフターゲット効果や
               近傍遺伝子への影響を排除できません。</p>
               <p>「変異で異常が出る」＋「戻すと回復する」の両方がそろって、
               はじめて因果を主張できます。</p>
               <p>したがって、適切でないのは <b>D</b> です。</p>`,
  },

  {
    id: "tec08", ...CAT,
    title: "顕微鏡の分解能", diff: 200, tags: ["分解能", "共焦点", "超解像"],
    explain: `<p>通常の光学顕微鏡の分解能が約 <b>200 nm</b> に制限される理由として、最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "レンズの製造精度の限界によるもので、技術の進歩でいずれ解決する。",
      "光の回折によるもので、使用する光の波長と対物レンズの開口数で決まる物理的な限界である。",
      "試料の厚さによる限界である。",
      "検出器の感度の限界である。",
    ],
    answer: 1,
    hints: [
      "アッベの式 d = λ / (2 NA) を思い出しましょう。何が分解能を決めていますか。",
      "可視光の波長は 400〜700 nm。その約半分が限界になります。",
    ],
    solution: `<p><b>【ステップ1】回折限界</b></p>
               <p>光は波なので、点光源も像面では有限の広がり
               （<b>エアリーディスク</b>）になります。
               2点が近すぎるとこの広がりが重なり、区別できません。</p>
               <p>アッベの式：<b>d = λ / (2 NA)</b></p>
               <ul>
                 <li>λ = 500 nm（緑色光）、NA = 1.4（油浸）とすると<br>
                     d = 500 / 2.8 ≒ <b>180 nm</b></li>
               </ul>
               <p>これは<b>物理法則による限界</b>であって、
               レンズの精度を上げても超えられません（A は誤り）。</p>
               <p><b>【ステップ2】改善の方向</b></p>
               <ul>
                 <li>λ を短く：紫外光、電子線（電子顕微鏡が 0.1 nm に届くのはこのため）</li>
                 <li>NA を大きく：油浸（n=1.5）で NA 1.4 程度が上限</li>
               </ul>
               <p><b>【ステップ3】超解像顕微鏡は限界をどう回避したか</b></p>
               <p>回折限界を「破った」のではなく、<b>迂回した</b>のがポイントです。</p>
               <table>
                 <tr><th>手法</th><th>考え方</th></tr>
                 <tr><td><b>STED</b></td><td>ドーナツ状のレーザーで周辺の蛍光を強制的に消し、<b>光る領域を絞る</b></td></tr>
                 <tr><td><b>PALM / STORM</b></td><td>1分子ずつ<b>時間をずらして</b>光らせ、重心位置を精密に決めて再構成する</td></tr>
                 <tr><td><b>SIM</b></td><td>既知の縞模様で照明し、モアレから高周波成分を復元する</td></tr>
               </table>
               <p>いずれも「同時に光っている点を近づけない」工夫であり、
               回折そのものは相変わらず起きています。</p>
               <p class="tiny">ベツィグ、ヘル、モーナーがこの功績で
               2014年のノーベル化学賞を受賞しました。</p>
               <p><b>【ステップ4】共焦点顕微鏡との区別</b></p>
               <p>共焦点顕微鏡はピンホールで焦点面外の光を除くもので、
               <b>コントラストと光学切片</b>を改善しますが、
               横方向の分解能は回折限界のままです。混同しやすい点です。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec09", ...CAT,
    title: "相関と因果を区別する", diff: 275, tags: ["データ解釈", "因果推論", "実験計画"],
    explain: `<p>ある研究で、「タンパク質 P の発現量が高い腫瘍ほど患者の予後が悪い」
              という<b>相関</b>が見つかった。</p>
              <p>「P ががんの悪性化を<b>引き起こしている</b>」と主張するために、
              最も必要な追加実験はどれか。</p>`,
    type: "radio",
    options: [
      "さらに多くの患者検体で同じ相関を確認する。",
      "がん細胞で P を人為的に増減させ、悪性形質（増殖・浸潤・造腫瘍能）が変化するかを調べる。",
      "P の立体構造を決定する。",
      "正常組織でも P が発現しているかを調べる。",
    ],
    answer: 1,
    hints: [
      "観察をいくら積み重ねても、相関は相関のままです。因果を示すには何が要るでしょうか。",
      "「原因とされるものを操作して、結果が変わるか」を見る必要があります。",
    ],
    solution: `<p><b>【ステップ1】相関から因果へ跳べない理由</b></p>
               <p>「P が高い ⇔ 予後が悪い」という観察には、少なくとも3つの解釈があります。</p>
               <ol>
                 <li><b>P → 悪性化</b>（P が原因）</li>
                 <li><b>悪性化 → P</b>（悪性度が高いから P が上がる。逆因果）</li>
                 <li><b>第三の因子 → 両方</b>（交絡）</li>
               </ol>
               <p>症例数を増やしても、この3つは区別できません（A が不十分な理由）。</p>
               <p><b>【ステップ2】介入実験が必要</b></p>
               <p>因果を示す唯一の方法は<b>操作すること</b>です。</p>
               <table>
                 <tr><th>操作</th><th>予測（P が原因なら）</th></tr>
                 <tr><td><b>ノックダウン／ノックアウト</b>（減らす）</td><td>増殖・浸潤・造腫瘍能が<b>低下</b></td></tr>
                 <tr><td><b>過剰発現</b>（増やす）</td><td>悪性形質が<b>亢進</b></td></tr>
                 <tr><td><b>レスキュー</b>（戻す）</td><td>表現型が<b>回復</b></td></tr>
               </table>
               <p>「減らすと弱まる、戻すと戻る」という<b>双方向の対応</b>が示せて、
               はじめて因果を主張できます。</p>
               <p><b>【ステップ3】それでも残る限界</b></p>
               <p>培養細胞での結果が生体で成り立つとは限りません。
               主張の強さは段階的に積み上げます。</p>
               <ol>
                 <li>細胞レベルの介入</li>
                 <li>マウスへの移植（in vivo での造腫瘍能・転移能）</li>
                 <li>遺伝子改変マウスでの自然発症モデル</li>
                 <li>ヒトでの介入（＝治療薬の臨床試験）</li>
               </ol>
               <p>最後の段階は、実は<b>究極の因果検証</b>でもあります。
               「P を阻害したら患者の予後が改善した」——
               これ以上に強い証拠はありません。</p>
               <p><b>【ステップ4】他の選択肢の位置づけ</b></p>
               <ul>
                 <li>C ⇒ 構造は創薬には重要ですが、因果の証明にはなりません。</li>
                 <li>D ⇒ 特異性の情報としては有用ですが、因果には触れません。</li>
               </ul>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec10", ...CAT,
    title: "シングルセル解析が明らかにするもの", diff: 305, tags: ["scRNA-seq", "不均一性", "データ解釈"],
    explain: `<p>ある組織を <b>バルクRNA-seq</b>（多数の細胞をまとめて解析）したところ、
              遺伝子 A と遺伝子 B の発現量がどちらも中程度だった。</p>
              <p>同じ組織を <b>シングルセルRNA-seq</b> で解析したときに得られうる知見として、
              バルク解析では原理的に得られないものはどれか。</p>`,
    type: "radio",
    options: [
      "組織全体での遺伝子 A の平均発現量",
      "A と B が同一の細胞で共発現しているのか、それぞれ別の細胞集団で発現しているのかの区別",
      "遺伝子 A の転写産物の全長配列",
      "組織に含まれる総RNA量",
    ],
    answer: 1,
    hints: [
      "バルク解析は「平均」を見ています。平均が同じでも、中身の分布は何通りもありえます。",
      "「中程度が一様に」なのか「高い細胞と低い細胞が半々」なのか、平均では区別できません。",
    ],
    solution: `<p><b>【ステップ1】平均が隠すもの</b></p>
               <p>バルクRNA-seq が測るのは<b>集団の平均</b>です。
               「A も B も中程度」という同じ結果を与える状況は複数あります。</p>
               <table>
                 <tr><th>実際の姿</th><th>バルクでの見え方</th></tr>
                 <tr><td>全細胞が A・B を中程度に発現</td><td>A 中、B 中</td></tr>
                 <tr><td>半数が A 高／B 低、残り半数が A 低／B 高</td><td><b>A 中、B 中（同じ！）</b></td></tr>
                 <tr><td>1割の細胞が A・B を極めて高発現、9割はゼロ</td><td>A 中、B 中（同じ）</td></tr>
               </table>
               <p>これらはバルクでは<b>原理的に区別できません</b>。
               細胞を混ぜた時点で情報が失われているからです。</p>
               <p><b>【ステップ2】シングルセルが答えられること</b></p>
               <ul>
                 <li><b>細胞集団の構成</b>：どんな細胞型が何割いるか</li>
                 <li><b>共発現の関係</b>：A と B が同じ細胞にあるか</li>
                 <li><b>希少集団の発見</b>：全体の1%しかいない細胞も検出できる</li>
                 <li><b>連続的な状態変化</b>：分化の途中経過（擬時間解析）</li>
               </ul>
               <p><b>【ステップ3】他の選択肢がバルクでも得られる理由</b></p>
               <ul>
                 <li>A ⇒ 平均発現量はまさにバルクが得意とするところです。
                     むしろ<b>バルクのほうが定量精度は高い</b>（scRNA-seq はドロップアウトが多い）。</li>
                 <li>C ⇒ 全長配列はバルクの long-read シークエンスのほうが有利です。
                     一般的な scRNA-seq（10x など）は 3' 末端しか読みません。</li>
                 <li>D ⇒ 総RNA量は組織全体の測定で得られます。</li>
                 </ul>
               <p><b>【ステップ4】使い分けの原則</b></p>
               <p>シングルセルは万能ではありません。</p>
               <table>
                 <tr><th></th><th>バルク</th><th>シングルセル</th></tr>
                 <tr><td>問い</td><td>「平均としてどうか」</td><td>「<b>どんな細胞がいるか</b>」</td></tr>
                 <tr><td>定量精度</td><td>高い</td><td>低い（ドロップアウト）</td></tr>
                 <tr><td>コスト</td><td>低い</td><td>高い</td></tr>
                 <tr><td>位置情報</td><td>なし</td><td>なし（<b>空間トランスクリプトミクス</b>が補う）</td></tr>
               </table>
               <p>なお解離の過程で細胞が受けるストレスや、
               解離しにくい細胞型が失われるバイアスにも注意が要ります。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec11", ...CAT,
    title: "ChIP-seq の手順", diff: 285, tags: ["ChIP", "クロマチン免疫沈降", "抗体"],
    explain: `<p>あるヒストン修飾がゲノム上のどこに分布するかを網羅的に調べたい。</p>
              <p><b>ChIP-seq</b>（クロマチン免疫沈降シーケンス）の手順として正しい順序はどれか。</p>`,
    type: "radio",
    options: [
      "ホルムアルデヒドで架橋 → クロマチンを断片化 → 目的の修飾に対する抗体で免疫沈降 → 架橋を解除しDNAを精製 → シーケンス → 参照ゲノムへマッピング",
      "クロマチンを断片化 → シーケンス → 抗体で免疫沈降 → 架橋 → マッピング",
      "抗体で免疫沈降 → 架橋 → クロマチンを断片化 → タンパク質を精製して質量分析",
      "RNAを抽出 → 逆転写 → 抗体で免疫沈降 → シーケンス",
    ],
    answer: 0,
    hints: [
      "まず「タンパク質とDNAをその場に固定する」必要があります。",
      "最後に読むのは DNA です。タンパク質を外してから読みます。",
    ],
    solution: `<p><b>【ステップ1】各段階の狙い</b></p>
               <table>
                 <tr><th>手順</th><th>何のために</th></tr>
                 <tr><td>1. <b>架橋</b>（ホルムアルデヒド）</td><td>タンパク質とDNAをその場で共有結合させ、後の操作で外れないようにする</td></tr>
                 <tr><td>2. <b>断片化</b>（超音波 または MNase）</td><td>200〜500 bp 程度に切る。<b>解像度はここで決まる</b></td></tr>
                 <tr><td>3. <b>免疫沈降</b></td><td>目的の修飾／タンパク質に対する<b>抗体</b>とビーズで、結合しているDNAごと回収</td></tr>
                 <tr><td>4. <b>架橋解除・精製</b></td><td>加熱して架橋を外し、タンパク質を分解してDNAだけを取り出す</td></tr>
                 <tr><td>5. <b>シーケンス</b></td><td>回収したDNA断片の配列を大量に読む</td></tr>
                 <tr><td>6. <b>マッピング</b></td><td>参照ゲノムに貼り付け、<b>ピーク</b>として濃縮領域を同定</td></tr>
               </table>
               <p><b>【ステップ2】結果の読み方</b></p>
               <p>reg22 で扱ったとおり、H3K4me3 のピークは<b>転写開始点</b>に、
               H3K4me1+H3K27ac のピークは<b>エンハンサー</b>に、
               CTCF のピークは<b>インスレーター／TAD境界</b>に立ちます。
               ES細胞で CTCF の結合部位を調べる、といった設問はこの読み方を問うています。</p>
               <p><b>【ステップ3】対照が要る</b></p>
               <p>抗体を加えないサンプルや input（免疫沈降前のDNA）を必ず並べます。
               開いたクロマチンは断片化されやすく、抗体に関係なく多く回収されるため、
               <b>input と比べて初めて「濃縮されている」と言える</b>からです。
               結果の信頼性は<b>抗体の特異性</b>にほぼ依存する点も、記述で触れると得点になります。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "tec12", ...CAT,
    title: "Hi-C とゲノムの三次元構造", diff: 295, tags: ["Hi-C", "3C", "TAD"],
    explain: `<p>Hi-C 法によって明らかになったゲノムの三次元構造について、正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "近接して存在するDNA断片どうしを連結してから配列を読み、接触頻度の行列を得る。頻繁に接触する領域のまとまりが TAD であり、より大きな単位として活性型と不活性型の2種類のコンパートメントが区別される。",
      "DNAの塩基配列をそのまま読むことで、核内での位置を直接決定する方法である。",
      "接触頻度は測れるが、TAD やコンパートメントのような区画構造は見つかっていない。",
      "コンパートメントは活性・不活性・中間の3種類に分けられ、細胞種によらず一定である。",
    ],
    answer: 0,
    hints: [
      "3C 法の考え方（架橋 → 切断 → 連結）を全ゲノムに広げた方法です。",
      "コンパートメントはいくつに分かれるか、そして細胞ごとに変わるかを考えてください。",
    ],
    solution: `<p><b>【ステップ1】原理</b></p>
               <ol>
                 <li>ホルムアルデヒドで架橋 → 空間的に近い部分が固定される</li>
                 <li>制限酵素で切断</li>
                 <li><b>ごく薄い条件で連結（ライゲーション）</b> → 空間的に近かった断片どうしがつながる</li>
                 <li>キメラ断片をシーケンスし、どの領域とどの領域がつながったかを数える</li>
                 <li>ゲノム全体の<b>接触頻度行列（コンタクトマップ）</b>を作る</li>
               </ol>
               <p>3C は「1対1」、4C は「1対多」、5C は「多対多（限定領域）」、
               <b>Hi-C は「全ゲノム対全ゲノム」</b>という関係です。</p>
               <p><b>【ステップ2】見えてくる階層</b></p>
               <table>
                 <tr><th>単位</th><th>大きさ</th><th>内容</th></tr>
                 <tr><td><b>コンパートメント</b></td><td>Mb 単位</td><td><b>A（活性）／B（不活性）の2種類</b>。<b>細胞種によって変わる</b></td></tr>
                 <tr><td><b>TAD</b></td><td>数十kb〜Mb</td><td>内部で頻繁に接触する区画。境界に <b>CTCF</b> とコヒーシン。細胞種を超えて比較的保存される</td></tr>
                 <tr><td>ループ</td><td>kb 単位</td><td>エンハンサー－プロモーター間の接触など</td></tr>
               </table>
               <p><b>【ステップ3】核内配置とのつながり</b></p>
               <p>転写が盛んな領域（コンパートメントA）は<b>核の内部寄り</b>に、
               不活性な領域（B）は<b>核膜近傍（ラミナ結合ドメイン）</b>に配置される傾向があります。
               また折りたたみ方は<b>細胞ごとに異なり</b>、集団の平均として見えている点にも注意が必要です。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "tec13", ...CAT,
    title: "突出末端に dNTP とポリメラーゼを加えると", diff: 300, tags: ["フィルイン", "突出末端", "DNAポリメラーゼ"],
    explain: `<p>制限酵素で切断して次の末端をもつ二本鎖DNAができた。</p>
              <p>ここに <b>4種類の dNTP と DNAポリメラーゼ</b>（5'→3' 重合活性のみ）を加えて十分に反応させた。
              最終的にどうなるか。</p>
              <pre>  5'-GATCCTAG        -3'
  3'-    GATCGGTA-5'</pre>`,
    type: "radio",
    options: [
      "5'突出になっている側は鋳型として使われて相補鎖が伸び、両端とも平滑末端になる。3'突出があってもポリメラーゼはそれを削れないため、3'突出側は残る。",
      "4種類のdNTPを加えると、突出末端はすべて分解されて平滑末端になる。",
      "DNAポリメラーゼは3'→5' 方向にも伸長できるため、どちらの突出も埋められる。",
      "dNTPを加えても何も起こらず、末端は変化しない。",
    ],
    answer: 0,
    hints: [
      "DNAポリメラーゼは 3'-OH がなければ伸長を始められず、方向は 5'→3' に限られます。",
      "「埋める（フィルイン）」ことはできても、「削る」ことは重合活性だけではできません。",
    ],
    solution: `<p><b>【ステップ1】ポリメラーゼにできること</b></p>
               <ul>
                 <li>既存の鎖の <b>3'-OH</b> にヌクレオチドを付け足す（<b>5'→3' のみ</b>）</li>
                 <li>相手の鎖を<b>鋳型</b>として読む（鋳型は 3'→5' に読まれる）</li>
                 <li>何もないところから始めることはできない</li>
               </ul>
               <p><b>【ステップ2】5'突出は埋まる</b></p>
               <p>5'突出末端では、へこんでいる側の鎖に<b>3'-OH があり、
               突出した相手鎖が鋳型として前方に存在します</b>。
               したがってポリメラーゼは3'末端から伸長し、突出部分と相補的な配列を書き込みます。
               これが<b>フィルイン（fill-in）反応</b>で、結果は<b>平滑末端</b>になります。</p>
               <p><b>【ステップ3】3'突出は埋まらない</b></p>
               <p>3'突出末端では、突出しているのが 3' 側です。
               その先には鋳型がないため伸長できず、
               またへこんでいる側の 5' 末端方向へは<b>原理的に伸ばせません</b>。
               重合活性だけでは削ることもできないので、<b>3'突出はそのまま残ります</b>。</p>
               <p>（実際の実験では、Klenow フラグメントや T4 DNAポリメラーゼがもつ
               <b>3'→5' エキソヌクレアーゼ活性</b>によって3'突出が削られ、平滑化されます。
               「重合活性だけなら残る／校正活性があれば削れる」という区別が問われます。）</p>
               <p><b>【まとめ】</b>5'突出＝埋まって平滑になる、3'突出＝重合活性だけでは残る。
               理由は「ポリメラーゼは 3'-OH から 5'→3' にしか伸ばせない」の一点に尽きます。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "tec14", ...CAT,
    title: "核型分析と染色体の記載法", diff: 145, tags: ["核型分析", "G分染法", "転座", "染色体異常"],
    explain: `<p>患者の末梢血リンパ球を培養し、コルヒチンで分裂中期に止めてから
              G分染法で染めた。結果は <b>46,XX,t(9;22)(q34;q11)</b> と記載された。</p>
              <p>この記載の読み方として正しいものはどれか。</p>`,
    type: "radio",
    options: [
      "染色体は46本、性染色体はXX、9番と22番の間の相互転座があり、切断点は9番長腕34領域と22番長腕11領域である。",
      "染色体は46本、9番と22番が欠失しており、短腕に異常がある。",
      "染色体は9本と22本の2種類しかなく、数的異常である。",
      "46本のうち9本と22本が三倍体になっている。",
    ],
    answer: 0,
    hints: [
      "「q」は長腕、「p」は短腕を表します。",
      "t( ) は translocation（転座）、その後の括弧は切断点の座標です。",
    ],
    solution: `<p><b>【ステップ1】記載の文法（ISCN）</b></p>
               <table>
                 <tr><th>部分</th><th>意味</th></tr>
                 <tr><td><b>46</b></td><td>染色体総数</td></tr>
                 <tr><td><b>XX</b></td><td>性染色体の構成</td></tr>
                 <tr><td><b>t(9;22)</b></td><td>9番と22番の<b>相互転座</b></td></tr>
                 <tr><td><b>(q34;q11)</b></td><td>切断点：9番の<b>長腕</b>34、22番の長腕11</td></tr>
               </table>
               <p>p = 短腕（petit）、q = 長腕。数字は動原体側から外側へ向かう領域・バンド番号です。</p>
               <p><b>【ステップ2】この核型が意味すること</b></p>
               <p>t(9;22)(q34;q11) は<b>フィラデルフィア染色体</b>で、
               9番の <i>ABL1</i> と22番の <i>BCR</i> が融合します（→ med07）。
               慢性骨髄性白血病の診断根拠であり、
               イマチニブという治療法の選択にも直結します。</p>
               <p><b>【ステップ3】核型分析の手順と限界</b></p>
               <ol>
                 <li>細胞を培養して分裂を促す（<b>分裂中期の細胞が必要</b>）</li>
                 <li>コルヒチン／コルセミドで紡錘体を壊し、中期に集積させる</li>
                 <li>低張液で膨らませ、スライドに落として展開</li>
                 <li>ギムザ染色（G分染）で縞模様を出し、対応づける</li>
               </ol>
               <p>数百万塩基以上の大きな変化しか見えず、分裂細胞が必要で、
               解像度は 5〜10 Mb 程度。
               微細な欠失や点変異は FISH・マイクロアレイ・シーケンスに任せます。
               一方で<b>ゲノム全体の構造を1枚で見渡せる</b>のは今も核型分析の強みです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },

  {
    id: "tec15", ...CAT,
    title: "FISH は何を可視化するのか", diff: 175, tags: ["FISH", "ハイブリダイゼーション", "プローブ", "染色体異常"],
    explain: `<p>蛍光 <i>in situ</i> ハイブリダイゼーション（FISH）で、
              HER2 遺伝子のシグナル数を数えたい。</p>
              <p>FISH の原理として正しい記述はどれか。</p>`,
    type: "radio",
    options: [
      "抗体で目的タンパク質を検出し、その局在を見る。",
      "蛍光標識した一本鎖核酸プローブを、標本中の相補的な配列に結合させて位置と数を見る。",
      "PCRで増幅した産物を電気泳動して長さを見る。",
      "染色体を丸ごと蛍光色素で染め、総量を測る。",
    ],
    answer: 1,
    hints: [
      "in situ とは「その場で」という意味です。細胞や染色体の形を保ったまま何を見るのでしょう。",
      "検出の特異性を生んでいるのは、塩基の相補性です。",
    ],
    solution: `<p><b>【ステップ1】原理は相補的な塩基対合</b></p>
               <p>標本（分裂中期の染色体、あるいは間期核・組織切片）を変性させて一本鎖にし、
               蛍光標識した<b>プローブ</b>をハイブリダイズさせます。
               洗浄して未結合プローブを除き、蛍光顕微鏡で観察します。
               <b>形態を保ったまま、特定の配列がどこに何個あるかがわかる</b>のが本質です。</p>
               <p><b>【ステップ2】検出できること</b></p>
               <table>
                 <tr><th>プローブの種類</th><th>わかること</th><th>例</th></tr>
                 <tr><td>遺伝子座特異的</td><td>コピー数の増減</td><td>HER2 増幅、微細欠失</td></tr>
                 <tr><td>2色スプリット／フュージョン</td><td><b>転座</b>の有無</td><td>BCR-ABL の融合シグナル</td></tr>
                 <tr><td>セントロメア／全染色体</td><td>異数性</td><td>21トリソミー</td></tr>
                 <tr><td>テロメア反復（Q-FISH）</td><td>テロメア長の相対値</td><td>個々の染色体末端の長さ</td></tr>
               </table>
               <p><b>【ステップ3】核型分析との補完関係</b></p>
               <p>FISH は<b>間期核でも使える</b>（＝細胞を培養して分裂させる必要がない）ため、
               ホルマリン固定標本や迅速診断に向きます。
               その代わり<b>プローブを当てた場所しか見えません</b>。
               「どこを疑うか」が決まっているときは FISH、
               全体を見渡したいときは核型分析やアレイ、という使い分けになります。</p>
               <p class="tiny">派生技術：多色 FISH（M-FISH／SKY）で全染色体を色分けする、
               DNA を引き伸ばして分解能を上げるファイバーFISH、
               RNA を検出して発現を1分子で見る RNA-FISH（smFISH）。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec16", ...CAT,
    title: "テロメア長をどう測るか", diff: 250, tags: ["テロメア", "TRF法", "Q-FISH", "qPCR", "測定法"],
    explain: `<p>ある研究で「加齢とともに白血球のテロメアが短縮する」ことを示したい。
              テロメア長の測定法として、TRF法（サザンブロット）・Q-FISH・qPCR（T/S比）がある。</p>
              <p>これらの比較として<b>誤っている</b>記述はどれか。</p>`,
    type: "radio",
    options: [
      "TRF法は制限酵素で処理した後にテロメアプローブでサザンブロットし、絶対的な長さ（kb）の分布を得られる。",
      "Q-FISH は個々の染色体末端ごとの長さを比較できるが、分裂中期の細胞が必要で手間がかかる。",
      "qPCR（T/S比）は少量DNAで大規模研究に向くが、得られるのは単一コピー遺伝子に対する相対値である。",
      "qPCR 法は絶対的な長さを kb 単位で直接与えるため、TRF法より正確で標準物質を必要としない。",
    ],
    answer: 3,
    hints: [
      "qPCR で測っているのは「テロメア反復の総量」と「参照遺伝子の量」の比です。",
      "相対値を長さに換算するには、既知の検体との対応づけが必要になります。",
    ],
    solution: `<p><b>【ステップ1】3つの方法が測っているもの</b></p>
               <table>
                 <tr><th>方法</th><th>測るもの</th><th>長所</th><th>短所</th></tr>
                 <tr><td><b>TRF</b>（サザン）</td><td>末端断片長の<b>分布（kb）</b></td><td>絶対値、金標準</td><td>DNA量が必要、サブテロメアを含む、労力大</td></tr>
                 <tr><td><b>Q-FISH</b></td><td>各末端の蛍光強度</td><td><b>染色体ごと・細胞ごと</b>に見える</td><td>中期細胞が必要、標準化が難しい</td></tr>
                 <tr><td><b>qPCR（T/S）</b></td><td>テロメア反復量 ÷ 単一コピー遺伝子量</td><td>微量DNA、高スループット</td><td><b>相対値のみ</b>、測定間変動が大きい</td></tr>
               </table>
               <p><b>【ステップ2】なぜ D が誤りか</b></p>
               <p>qPCR 法（Cawthon 法）の出力は <b>T/S 比という無次元の相対量</b>です。
               kb に換算したい場合は、TRF法で長さを決めた検体を標準として
               検量線を作らなければなりません。
               「標準物質なしで絶対値が出る」ということはありません。</p>
               <p><b>【ステップ3】研究デザインへの含意</b></p>
               <p>数千人規模のコホートで加齢との関連を見るなら、少量の血液DNAで済む qPCR が現実的です。
               ただし相対値なので、<b>同一バッチ内での比較</b>に限り、
               参照検体を各プレートに入れて補正する必要があります。
               一方「どの染色体のテロメアが短いのか」「集団内に極端に短い細胞があるか」を
               問うなら Q-FISH でなければ答えられません。</p>
               <p><b>測定法は問いによって選ぶ</b> ─ 精度の高低ではなく、
               何を出力するかで決まります。</p>
               <p>したがって、<b>誤っている</b>のは <b>D</b> です。</p>`,
  },

  {
    id: "tec17", ...CAT,
    title: "ベクターの搭載容量と人工染色体", diff: 210, tags: ["ベクター", "BAC", "YAC", "人工染色体", "クローニング"],
    explain: `<p>約 500 kb のヒトゲノム領域を、内部の調節配列ごと1つのクローンとして
              哺乳類細胞に導入したい。</p>
              <p>ベクターの選択として最も適切なものはどれか。</p>`,
    type: "radio",
    options: [
      "通常のプラスミド（搭載容量 〜10 kb）を使い、断片に分けて別々に導入する。",
      "BAC や YAC のような大容量ベクター、あるいはヒト人工染色体（HAC）を用いる。",
      "PCR 産物をそのまま導入する。",
      "レトロウイルスベクター（搭載容量 〜8 kb）を用いる。",
    ],
    answer: 1,
    hints: [
      "まず 500 kb が載るかどうかで候補が絞られます。",
      "調節配列を含む長い領域を「ひとまとまりで」保ちたいという条件が鍵です。",
    ],
    solution: `<p><b>【ステップ1】搭載容量で候補を絞る</b></p>
               <table>
                 <tr><th>ベクター</th><th>宿主</th><th>搭載容量の目安</th></tr>
                 <tr><td>プラスミド</td><td>大腸菌</td><td>〜10 kb</td></tr>
                 <tr><td>λファージ／コスミド</td><td>大腸菌</td><td>15〜45 kb</td></tr>
                 <tr><td><b>BAC</b>（細菌人工染色体）</td><td>大腸菌</td><td><b>100〜300 kb</b></td></tr>
                 <tr><td><b>YAC</b>（酵母人工染色体）</td><td>酵母</td><td><b>〜1000 kb 以上</b></td></tr>
                 <tr><td><b>HAC</b>（ヒト人工染色体）</td><td>ヒト細胞</td><td>Mb 級</td></tr>
               </table>
               <p>500 kb を1クローンで扱えるのは YAC 以上、
               BAC でも連結（BACを2つつなぐ）が要ります。
               レトロウイルスは容量が小さく、この用途には使えません。</p>
               <p><b>【ステップ2】人工染色体に必要な3要素</b></p>
               <p>YAC が「染色体」として酵母内で維持されるには、
               天然の染色体と同じ部品が必要です。</p>
               <ul>
                 <li><b>複製起点（ARS）</b>：複製されるため</li>
                 <li><b>セントロメア（CEN）</b>：分裂時に娘細胞へ正しく分配されるため</li>
                 <li><b>テロメア（TEL）</b>：両端が分解・融合しないため</li>
               </ul>
               <p>この3つを人工的に組み合わせて機能する染色体が作れることを示したのが、
               染色体の必要条件を実験的に確かめた仕事でもあります。</p>
               <p><b>【ステップ3】なぜ「ひとまとまり」が要るのか</b></p>
               <p>遺伝子の発現には、遠く離れたエンハンサーや LCR、
               インスレーターとの位置関係が必要です。
               断片に分けて別々に導入すると、この関係が壊れ、
               生理的な発現量・組織特異性が再現されません。
               HAC は宿主ゲノムに挿入されない<b>エピソーム型</b>で維持されるため、
               挿入変異のリスクを避けつつ大きな領域を1コピーで保てます。</p>
               <p class="tiny">HAC は完全ヒト抗体産生マウス（ヒト抗体遺伝子座を Mb 単位で保持）などに応用されています。</p>
               <p>したがって、正解は <b>B</b> です。</p>`,
  },

  {
    id: "tec18", ...CAT,
    title: "細胞を不死化する実験を設計する", diff: 285, tags: ["不死化", "hTERT", "SV40", "実験設計", "対照"],
    explain: `<p>正常ヒト線維芽細胞は約50回の分裂で老化する。
              長期に使える細胞株を作るため、次の2つの操作を比べた。</p>
              <p>操作①：<b>hTERT</b>（テロメラーゼ触媒サブユニット）を導入する<br>
              操作②：<b>SV40 large T 抗原</b>（p53 と Rb を不活化する）を導入する</p>
              <p>「正常に近い性質を保った細胞株」を得たいとき、より適切な操作と理由はどれか。</p>`,
    type: "radio",
    options: [
      "①。テロメア短縮という停止の原因を取り除くため、染色体を安定に保ったまま分裂を続けられる。",
      "②。チェックポイントを外すほうが確実に増えるので、性質も正常に近い。",
      "①と②は同義であり、どちらでも結果は同じ。",
      "どちらも不可。不死化には必ずがん遺伝子 RAS の導入が要る。",
    ],
    answer: 0,
    hints: [
      "老化の「原因」と、それを「検知して止める装置」のどちらに手を入れるかの違いです。",
      "p53 と Rb を壊した細胞では、その後の染色体はどうなるでしょうか（dna15 を思い出しましょう）。",
    ],
    solution: `<p><b>【ステップ1】2つの操作は階層が違う</b></p>
               <table>
                 <tr><th></th><th>hTERT 導入</th><th>large T 導入</th></tr>
                 <tr><td>手を入れる場所</td><td><b>原因</b>（テロメア短縮）</td><td><b>応答</b>（p53/Rb 経路）</td></tr>
                 <tr><td>テロメア</td><td>維持される</td><td>短縮が進む</td></tr>
                 <tr><td>染色体</td><td>比較的安定</td><td><b>不安定化・異数性</b></td></tr>
                 <tr><td>DNA損傷応答</td><td>保たれる</td><td>失われる</td></tr>
                 <tr><td>形質転換</td><td>それだけでは起こらない</td><td>造腫瘍性へ近づく</td></tr>
               </table>
               <p>hTERT だけを入れた細胞は、接触阻害・足場依存性・p53 応答を保ったまま
               無限に分裂します。<b>老化はしないが、がん化はしていない</b>状態です。</p>
               <p><b>【ステップ2】large T の副作用</b></p>
               <p>p53/Rb を外すと確かに増えますが、チェックポイントを失った細胞は
               テロメア短縮に応答できず、末端融合と切断融合橋サイクルを起こします（→ dna15）。
               得られた細胞株の核型は元の細胞と大きく異なり、
               <b>「正常細胞の代用」としての妥当性が失われます</b>。</p>
               <p><b>【ステップ3】実験に必要な対照と確認</b></p>
               <ul>
                 <li><b>ベクターのみ導入</b>した細胞：予定どおり老化することを確認</li>
                 <li>TRAP アッセイでテロメラーゼ活性を、TRF法でテロメア長を確認</li>
                 <li>核型分析／アレイで染色体が保たれていることを確認</li>
                 <li>軟寒天コロニー形成（足場非依存性）が陰性であることを確認 ─ 形質転換していない証拠</li>
                 <li>目的の機能（分化能、マーカー発現）が元の細胞と一致するか</li>
               </ul>
               <p>「増え続けること」自体は目的ではなく、
               <b>元の細胞の性質を保ったまま増え続けること</b>が目的です。
               何を確認するかまで設計に含めるのが要点です。</p>
               <p class="tiny">なお細胞種によっては hTERT だけでは不死化せず（上皮細胞では p16 の蓄積が停止を招く）、
               CDK4 の導入などが併用されます。「テロメア以外の停止機構もある」ことの裏返しです。</p>
               <p>したがって、正解は <b>A</b> です。</p>`,
  },
];
